import {
  fetchServicepartnersWithoutPagination,
  type ServicePartnerBrief,
} from "@/lib/api/resources/servicePartners";
import { UseMutationResult, useQuery } from "@tanstack/react-query";
import { Button, Card, Input, Select } from "@rumbledirect/ui";
import { Col, Form, Row } from "antd";
import { useEffect } from "react";
import { CompanyServicePartnersTable } from "./CompanyServicePartnersTable";
import { CompanyEditValues } from "../CompanyEdit";

interface CompanyFormCardProps {
  mutation: UseMutationResult<unknown, Error, CompanyEditValues, unknown>;
  loading?: boolean;
  initialValues?: CompanyEditValues;
  title?: string;
  buttonText?: string;
}

export const CompanyFormCard = ({
  mutation,
  loading,
  initialValues,
  title = "Bedrijf aanmaken",
  buttonText = "Aanmaken",
}: CompanyFormCardProps) => {
  const [form] = Form.useForm();

  const servicePartnersQuery = useQuery({
    queryKey: ["servicePartners", "brief"],
    queryFn: async () => {
      const servicePartners = await fetchServicepartnersWithoutPagination();
      return servicePartners.map((servicePartner: ServicePartnerBrief) => ({
        fullName: servicePartner.fullName,
        value: servicePartner.id,
      }));
    },
  });

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={mutation.mutate}
      initialValues={initialValues}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card loading={loading} title={title}>
            <Form.Item label="Groepsnaam" name="groupName">
              <Input />
            </Form.Item>
            <Form.Item
              rules={[{ required: true, message: "Dit veld is verplicht." }]}
              label="Naam"
              name="name"
            >
              <Input />
            </Form.Item>
            <Form.Item label="Type" name="type">
              <Select
                options={[
                  {
                    value: "lease",
                    label: "Leasebedrijf",
                  },
                  {
                    value: "insurance",
                    label: "Verzekeringsmaatschappij",
                  },
                  {
                    value: "dealership",
                    label: "Autodealer",
                  },
                ]}
              />
            </Form.Item>
            <Form.Item label="E-mail" name="email">
              <Input type="email" />
            </Form.Item>
            <Form.Item label="Telefoon" name="telephone">
              <Input />
            </Form.Item>

            {/* Address */}
            <Card size="small" title="Adres" style={{ marginBottom: 16 }}>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name={["address", "street"]} label="Straat">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["address", "streetNumber"]} label="Huisnr">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name={["address", "streetNumberAddition"]}
                    label="Toevoeging"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["address", "postalCode"]} label="Postcode">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item name={["address", "city"]} label="Plaats">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Postal Address */}
            <Card size="small" title="Postadres" style={{ marginBottom: 16 }}>
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item name={["postalAddress", "street"]} label="Straat">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["postalAddress", "streetNumber"]}
                    label="Huisnr"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item
                    name={["postalAddress", "streetNumberAddition"]}
                    label="Toevoeging"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["postalAddress", "postalCode"]}
                    label="Postcode"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item name={["postalAddress", "city"]} label="Plaats">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Form.Item label={null}>
              <Button
                loading={mutation.isPending}
                type="primary"
                htmlType="submit"
              >
                {buttonText}
              </Button>
            </Form.Item>
          </Card>
        </Col>

        <Col span={12}>
          <Card
            loading={loading || servicePartnersQuery.isLoading}
            title="Herstellers koppelen"
          >
            <Form.Item name="servicePartners">
              <CompanyServicePartnersTable
                servicePartners={servicePartnersQuery.data}
                editable
              />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    </Form>
  );
};
